import React, { useCallback, useEffect, useState } from 'react'
import { IconSearch, IconTrash } from '@tabler/icons-react'
import authQueries from '../../queries/authQueries'
import Empty from '../../components/Empty'
import DrawerComponent from '../../components/DrawerComponent'
import ChangePassword from './ChangePassword'
import { snackbarToast } from '../../components/SnackbarToast'
import Loader from '../../components/Loader'
import { useSelector } from 'react-redux'
import PaginationComponent from '../../components/PaginationComponent'
import { useApi } from '../../utils/ApiProvider'
import dropdownQueries from '../../queries/dropdownQueries'
import ConfirmationModal from '../../components/ConfirmationModal'
import debounce from 'lodash/debounce'


const UserList = () => {

    const [result, setResult] = useState([])
    const [dropdownResult, setDropdownResult] = useState([])
    const [loader, setLoader] = useState(true)

    const page = useSelector((state)=> state.pagination.page)
    const size = useSelector((state)=> state.pagination.size)
    const { apiData, setApiDataAndNotify } = useApi()

    const [search, setSearch] = useState('')
    const [role, setRole] = useState(null)
    const [vessel, setVessel] = useState(null)

    const getData = authQueries.useUserList(
        (res)=>{
            if(res?.success){
                setResult(res?.data)
            }
            setLoader(false)
        }
    )

    const debouncedSearch = useCallback(
        debounce((text, vessel, role, page, size) => {
            getData.mutateAsync({search: text, vessel, role, page, size})
        }, 300),
        []
    );
  
    useEffect(() => {
        debouncedSearch(search, vessel, role, page, size);
        return () => {
            debouncedSearch.cancel();
        };
    }, [search, vessel, role, page, size, apiData]);

    const dropdown = dropdownQueries.useUserListDropdown(
        (res)=>{
            if(res?.success){
                setDropdownResult(res)
            }
        }
    )

    useEffect(() => {
        dropdown.mutateAsync()
    }, []);

    const inputCreateFields = [
        {
            name: 'username',
            type: 'text',
            label: 'Username',
            placeholder: 'Username',
            required: true
        },
        {
            name: 'name',
            type: 'text',
            label: 'Name',
            placeholder: 'Name',
            required: true
        },
        {
            name: 'email',
            type: 'email',
            label: 'Email',
            placeholder: 'Email',
            required: true,
        },
        {
            name: 'password',
            type: 'password',
            label: 'Password',
            placeholder: 'Password',
            required: true,
            min: 6,
            max: 32
        },
        {
            name: 'mobile',
            type: 'number',
            label: 'Mobile No.',
            placeholder: 'Mobile No.',
            required: false
        },
        {
            name: 'roles',
            type: 'tag',
            label: 'Role',
            placeholder: 'Role',
            required: true,
            array: true,
            options: dropdownResult?.roles
        },
        {
            name: 'vessels',
            type: 'tag',
            label: 'Vessel',
            placeholder: 'Vessel',
            required: true,
            array: true,
            options: dropdownResult?.vessels
        }
    ]    
    
    const inputUpdateFields = [
        {
            name: 'username',
            type: 'text',
            label: 'Username',
            placeholder: 'Username',
            required: true
        },
        {
            name: 'name',
            type: 'text',
            label: 'Name',
            placeholder: 'Name',
            required: true
        },
        {
            name: 'email',
            type: 'email',
            label: 'Email',
            placeholder: 'Email',
            required: true,
        },
        {
            name: 'mobile',
            type: 'number',
            label: 'Mobile No.',
            placeholder: 'Mobile No.',
            required: false
        },
        {
            name: 'roles',
            type: 'tag',
            label: 'Role',
            placeholder: 'Role',
            required: true,
            array: true,
            options: dropdownResult?.roles
        },
        {
            name: 'vessels',
            type: 'tag',
            label: 'Vessel',
            placeholder: 'Vessel',
            required: true,
            array: true,
            options: dropdownResult?.vessels
        }
    ]
    
    const changePasswordInput = [
        {
            name: 'password',
            type: 'password',
            label: 'Password',
            placeholder: 'Password',
            required: true
        },
    ]
    
    
    const create = authQueries.useCreateUser(
        (res)=>{
            if(res?.success){
                setApiDataAndNotify(new Date())
                snackbarToast('User Created Successfully', 'success')
            }
            if(!res?.success){
                if(res?.error?.length === 0){
                    snackbarToast(res?.message, 'error')
                    return
                }

                const errors = res?.error?.map((data) => {
                    if (data?.email) {
                        return 'Email already exists';
                    }
                    return null;
                }).filter(Boolean);
                
                errors?.forEach((data) => snackbarToast(data, 'error'));
                
            }
        }
    )

    const update = authQueries.useUpdateUser(
        (res)=>{
            if(res?.success){
                setApiDataAndNotify(new Date())
                snackbarToast('User Updated Successfully', 'success')
            }
            if(!res?.success){
                if(res?.errors?.length === 0){
                    snackbarToast(res?.message, 'error')
                    return
                }

                const errors = res?.error?.map((data) => {
                    if (data.email) {
                        return 'Email already exists';
                    }
                    return null;
                }).filter(Boolean);
                
                errors?.forEach((data) => snackbarToast(data, 'error'));
                
            }
        }
    )

    const changePassword = authQueries.useChangePassword(
        (res)=>{
            if(res?.success){
                snackbarToast('Password Changed Successfully', 'success')
            }
        }
    )

    const deleteUser = authQueries.useDeleteUser(
        (res)=>{
            if(res?.success){
                setApiDataAndNotify(new Date())
                snackbarToast('User Deleted Successfully', 'success')
            }
        }
    )

    const handleDelete =(data)=>{
        deleteUser.mutateAsync(data?.id)
    }

  return (
    <div className='card-shadow m-4 max-[768px]:m-2 bg-white rounded-[10px]'>

        <div className='flex justify-between max-[934px]:flex-col items-center p-6 gap-y-3'>
            <div>
                <p className='text-[13px] text-[#6D6B77] font-medium'>Showing {result?.totalItems} entries</p>
            </div>
            <div className='flex gap-2 max-[799px]:flex-col max-[799px]:w-full'>
                <select onChange={(e)=>{setRole(e.target.value)}} className='w-[200px] max-[799px]:w-full h-[40px] border rounded-[7px] px-3 mt-[1px] text-[13px]'>
                    <option value="">Select Role</option>
                    {
                        dropdownResult?.roles?.map((opt, index) =>(
                            <option key={index} value={opt?.id}>{opt?.name}</option>
                        ))
                    }
                </select>
                <select onChange={(e)=>{setVessel(e.target.value)}} className='w-[200px] max-[799px]:w-full h-[40px] border rounded-[7px] px-3 mt-[1px] text-[13px]'>
                    <option value="">Select Vessel</option>
                    {
                        dropdownResult?.vessels?.map((opt, index) =>(
                            <option key={index} value={opt?.id}>{opt?.name}</option>
                        ))
                    }
                </select>
                <div className='relative'>
                    <input onChange={(e)=>{setSearch(e.target.value)}} placeholder='Type to search...' className='w-[300px] max-[799px]:w-full h-[40px] border rounded-[7px] pl-10 pr-3 mt-[1px] text-[13px]' />
                    <IconSearch className='absolute top-[12px] left-4 text-secondary' size={16} />
                </div>
                <DrawerComponent entityForm={create} inputFields={inputCreateFields} btnName={'Add User'} header={'Add User'} />
            </div>
        </div>

        {
            loader ?
            <div className="w-full flex items-center justify-center py-40">
                <Loader />
            </div>:
            <div className='px-6 pb-6 overflow-x-auto'>
                {
                    result?.totalItems > 0 ?
                        <table className='table w-full'>
                            <thead>
                                <tr className='table-row'>
                                    <th>Sl No.</th>
                                    <th>Full Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Roles</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {result?.items?.map((row, index) => {
                                    return(
                                        <tr key={index} className='table-row'>
                                            <td>{result?.currentPage * 10 + index + 1}</td>
                                            <td>{row?.name}</td>
                                            <td>{row?.email}</td>
                                            <td>{row?.mobile}</td>
                                            <td>{row?.roles?.map((role, index) => <div key={index}>{role?.label}</div>)}</td>
                                            <td>
                                                <div className='flex gap-2 justify-end'>
                                                    <ChangePassword entityForm={changePassword} inputFields={changePasswordInput} btnName={'Change Password'} header={'Change Password'} item={row} />
                                                    <DrawerComponent entityForm={update} inputFields={inputUpdateFields} btnName={'Update User'} header={'Update User'} item={row} edit={true} user />
                                                    <ConfirmationModal data={row} handle={handleDelete} color="danger" buttonContent="Delete" content={"Are you sure you want to delete this user?"}>
                                                        <IconTrash size="16" />
                                                    </ConfirmationModal>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                )}
                            </tbody>
                        </table>:
                    <Empty />
                }
                <PaginationComponent result={result} />
            </div>
        }


    </div>
  )
}

export default UserList